import { addDays, differenceInCalendarDays, format, isValid, parse, startOfDay } from 'date-fns';

export type DateRangeValue = {
  from: Date | null;
  to: Date | null;
};

export type ApiDateRange = {
  from?: string;
  to?: string;
};

const API_DATE_FORMAT = 'yyyy-MM-dd';
const CHIP_DATE_FORMAT = 'dd/MM/yyyy';

export function emptyDateRange(): DateRangeValue {
  return { from: null, to: null };
}

export function isDateRangeEmpty(value: DateRangeValue): boolean {
  return !value.from && !value.to;
}

export function isDateRangeActive(value: DateRangeValue): boolean {
  return Boolean(value.from || value.to);
}

export function clampDateRange(value: DateRangeValue): DateRangeValue {
  if (value.from && value.to && value.from > value.to) {
    return { from: value.from, to: value.from };
  }
  return value;
}

/** Inclusive calendar days between from and to; null when either end is missing. */
export function inclusiveDayCount(value: DateRangeValue): number | null {
  if (!value.from || !value.to) return null;
  return differenceInCalendarDays(startOfDay(value.to), startOfDay(value.from)) + 1;
}

/** Caps a complete range so it is at most `maxSpanDays` inclusive days (keeps `from`). */
export function clampDateRangeToMaxSpan(
  value: DateRangeValue,
  maxSpanDays: number
): DateRangeValue {
  if (!value.from || !value.to || maxSpanDays < 1) return value;
  const from = startOfDay(value.from);
  const to = startOfDay(value.to);
  const span = differenceInCalendarDays(to, from) + 1;
  if (span <= maxSpanDays) return { from, to };
  return { from, to: addDays(from, maxSpanDays - 1) };
}

export function toApiDateRange(value: DateRangeValue): ApiDateRange {
  const result: ApiDateRange = {};
  if (value.from) {
    result.from = format(value.from, API_DATE_FORMAT);
  }
  if (value.to) {
    result.to = format(value.to, API_DATE_FORMAT);
  }
  return result;
}

function parseApiDate(value?: string | null): Date | null {
  if (!value?.trim()) {
    return null;
  }
  const parsed = parse(value.trim(), API_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
}

export function parseDateRangeFromApi(
  from?: string | null,
  to?: string | null,
): DateRangeValue {
  return clampDateRange({
    from: parseApiDate(from),
    to: parseApiDate(to),
  });
}

function formatChipDate(date: Date): string {
  return format(date, CHIP_DATE_FORMAT);
}

export function formatDateRangeChip(value: DateRangeValue): string {
  if (value.from && value.to) {
    return `${formatChipDate(value.from)} – ${formatChipDate(value.to)}`;
  }
  if (value.from) {
    return formatChipDate(value.from);
  }
  if (value.to) {
    return formatChipDate(value.to);
  }
  return '';
}
